/**
 * Builds structured search filter trees for the Case Appeals table.
 *
 * `pharmacy/appeal/list` compiles these with the same predicate compiler the
 * dashboard's other search boxes use, so `contains` and `is_any_of` mean the
 * same thing here as on the oncology submission tables.
 */

import {
  AppealFilterCondition,
  AppealFilterGroup,
} from "../../../data-model/caseAppealsTable";

/** Columns a typed search term is matched against. */
const SEARCH_COLUMNS = ["patient_name", "patient_mrn", "seq"];

/**
 * Builds the OR search tree matching patient name, MRN, or Rx number.
 *
 * @param searchTerm - Raw search text from the table search input
 * @returns AppealFilterGroup: OR group of contains conditions
 */
export const buildSearchFilterTree = (
  searchTerm: string,
): AppealFilterGroup => {
  const trimmed = searchTerm.trim();

  return {
    type: "group",
    operator: "or",
    children: SEARCH_COLUMNS.map((field) => ({
      type: "condition" as const,
      field,
      operator: "contains",
      value: trimmed,
    })),
  };
};

/**
 * Converts the flat sidebar filter map into predicate condition nodes.
 *
 * @param apiFilters - Filter map from processAppealFilterData
 * @returns AppealFilterCondition[]: One is_any_of condition per active filter
 */
export const convertLegacyFiltersToConditions = (
  apiFilters: Record<string, string[]>,
): AppealFilterCondition[] => {
  return Object.entries(apiFilters)
    .filter(([, values]) => values && values.length > 0)
    .map(([field, values]) => ({
      type: "condition" as const,
      field,
      operator: "is_any_of",
      value: values,
    }));
};

/**
 * Merges the search tree with any active sidebar filters.
 *
 * @param searchTerm - Raw search text from the table search input
 * @param apiFilters - Filter map from processAppealFilterData
 * @returns AppealFilterGroup: AND of sidebar filters and the search tree, or the tree alone
 */
export const mergeSearchFiltersWithSidebarFilters = (
  searchTerm: string,
  apiFilters: Record<string, string[]>,
): AppealFilterGroup => {
  const searchTree = buildSearchFilterTree(searchTerm);
  const sidebarConditions = convertLegacyFiltersToConditions(apiFilters);

  if (sidebarConditions.length === 0) {
    return searchTree;
  }

  return {
    type: "group",
    operator: "and",
    children: [...sidebarConditions, searchTree],
  };
};
